import { useNavigate } from "react-router-dom";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Clock, CheckCircle, Search, Calendar, Droplets, Wrench } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { toast } from "sonner";

// Fix: "Add to Calendar" built a blob: URL and called window.open(url) on it.
// That works in a normal desktop/mobile browser, but inside the Capacitor
// native WebView (iOS WKWebView / Android WebView) window.open on a blob URL
// is unreliable — it's frequently blocked or silently does nothing, so the
// button appeared to do nothing on the actual app. On native, write the .ics
// to a temp file and hand it to the native Share sheet instead, which lets
// the user save it straight into Calendar/Files — the standard Capacitor
// pattern for this without needing a dedicated calendar plugin.
const addToCalendar = async (eventTitle, eventDescription, dueDate) => {
    let startDate = new Date();
    if (dueDate) {
        startDate = new Date(dueDate);
    } else {
        startDate.setDate(startDate.getDate() + 7);
    }

    const startStamp = startDate.toISOString().replace(/-|:|\.\d\d\d/g, "");
    const endStamp = new Date(startDate.getTime() + 60 * 60 * 1000).toISOString().replace(/-|:|\.\d\d\d/g, "");

    const icsContent = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'BEGIN:VEVENT',
        `DTSTART:${startStamp}`,
        `DTEND:${endStamp}`,
        `SUMMARY:${eventTitle}`,
        `DESCRIPTION:${eventDescription}`,
        'END:VEVENT',
        'END:VCALENDAR'
    ].join('\n');

    if (Capacitor.isNativePlatform()) {
        try {
            const fileName = `homeezi-event-${Date.now()}.ics`;
            await Filesystem.writeFile({
                path: fileName,
                data: icsContent,
                directory: Directory.Cache,
                encoding: Encoding.UTF8,
            });
            const { uri } = await Filesystem.getUri({
                path: fileName,
                directory: Directory.Cache,
            });
            await Share.share({
                title: eventTitle,
                url: uri,
                dialogTitle: "Add to Calendar",
            });
        } catch (error) {
            console.error("Add to Calendar (native) failed:", error);
            toast.error("Couldn't open the share sheet. Please try again.");
        }
        return;
    }

    // Web fallback: unchanged blob + window.open, which works fine outside
    // the native WebView.
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    window.open(url);
};

const ActionDetailModal = ({ open, onOpenChange, item, onLogService, onMarkWatered, onMarkSubTaskDone }) => {
    const navigate = useNavigate();

    if (!item) return null;

    const getCategoryFromTask = (title) => {
        const t = title.toLowerCase();
        if (t.includes("geyser") || t.includes("plumbing")) return "plumbing";
        if (t.includes("smoke alarm") || t.includes("electrical")) return "electrical";
        if (t.includes("roof")) return "roofing";
        return "general";
    };

    const handleFindPro = () => {
        // Pretty name for display (e.g., "Plumbing" or "Garden & Pool")
        const displayCategory = item.category || getCategoryFromTask(item.title);

        // Slug for filtering (e.g., "plumbing" or "garden-pool")
        const filterSlug = displayCategory.toLowerCase().replace(/ & /g, "-").replace(/\s+/g, "-");

        onOpenChange(false);
        navigate("/directory", {
            state: {
                filterCategory: filterSlug,
                displayCategory: displayCategory
            }
        });
    };

    const handleLogService = () => {
        onLogService(item);
        onOpenChange(false);
    };

    const getUrgencyConfig = (urgency) => {
        switch (urgency) {
            case "high":
                return {
                    icon: AlertTriangle,
                    color: "text-destructive",
                    bgColor: "bg-destructive/10",
                    label: "Overdue",
                };
            case "medium":
                return {
                    icon: Clock,
                    color: "text-amber-500",
                    bgColor: "bg-amber-500/10",
                    label: "Due Soon",
                };
            default:
                return {
                    icon: CheckCircle,
                    color: "text-emerald-500",
                    bgColor: "bg-emerald-500/10",
                    label: "Recommended",
                };
        }
    };

    const urgencyConfig = getUrgencyConfig(item.urgency);
    const UrgencyIcon = urgencyConfig.icon;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-3xl max-h-[85vh] overflow-y-auto overflow-x-hidden w-[90%] max-w-md mx-auto p-6">
                <DialogHeader className="text-center">
                    <div className={`mx-auto p-3 rounded-full ${urgencyConfig.bgColor} mb-2`}>
                        <UrgencyIcon className={`w-6 h-6 ${urgencyConfig.color}`} />
                    </div>
                    <DialogTitle className="text-xl">{item.title}</DialogTitle>
                    <DialogDescription className="text-base">
                        {item.dueText}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 pt-4">
                    {item.type === 'plant' ? (
                        <>
                            <Button
                                onClick={() => {
                                    onMarkWatered(item.originalId);
                                    onOpenChange(false);
                                }}
                                variant="outline"
                                className="w-full rounded-xl py-5 border-emerald-500/20 bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500 hover:text-white"
                            >
                                <Droplets className="w-4 h-4 mr-2" />
                                Mark as Watered
                            </Button>
                        </>
                    ) : item.type === 'appliance-subtask' ? (
                        <>
                            <Button
                                onClick={() => {
                                    onMarkSubTaskDone(item.applianceId, item.subTaskId, item.subTaskName);
                                    onOpenChange(false);
                                }}
                                variant="outline"
                                className="w-full rounded-xl py-5 border-emerald-500/20 bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500 hover:text-white font-bold"
                            >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Mark as Done
                            </Button>
                        </>
                    ) : item.type === 'warranty' ? (
                        <>
                            <Button
                                onClick={() => addToCalendar(`Warranty Expiry: ${item.title}`, item.dueText, item.dueDate)}
                                variant="secondary"
                                className="w-full rounded-xl py-5 border-amber-500/20 bg-amber-500/10 text-amber-600 hover:bg-amber-500 hover:text-white"
                            >
                                <Calendar className="w-4 h-4 mr-2" />
                                Add Reminder to Calendar
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                onClick={handleLogService}
                                variant="outline"
                                className="w-full rounded-xl py-5"
                            >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Log a Service
                            </Button>
                            <Button
                                onClick={() => addToCalendar(`Service: ${item.title}`, item.dueText, item.dueDate)}
                                variant="secondary"
                                className="w-full rounded-xl py-5"
                            >
                                <Calendar className="w-4 h-4 mr-2" />
                                Add to Calendar
                            </Button>
                            <Button
                                onClick={handleFindPro}
                                className="w-full rounded-xl py-5"
                            >
                                <Search className="w-4 h-4 mr-2" />
                                Find a Pro
                            </Button>
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ActionDetailModal;
